// ─────────────────── React & Packages ────────────────────────
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

// ─────────────────── UI Components ───────────────────────────
import Modal from "../../common/Modal";
import PrimaryButton, { PrimaryButtonType } from "../../common/PrimaryButton";
import OutlinedButton from "../../common/OutlinedButton";
import Spinner from "../../common/Spinner";

// ─────────────────── Styles ──────────────────────────────────
import styles from "./DeleteModal.module.scss";

// ─────────────────── Zustand Store ───────────────────────────
import { useOrganisationStore } from "../../../providers/organisation/organisation.provider";

interface Props {
  showModal: boolean;
  closeModal: () => void;
}

const DeleteModal = ({ showModal, closeModal }: Props) => {
  const { t } = useTranslation();

  // ─── Zustand Selectors ─────────────────────────────────────
  const selectedOrganisation = useOrganisationStore(
    (state) => state.selectedOrganisation
  );
  const deleteOrganisation = useOrganisationStore(
    (state) => state.deleteOrganisation
  );

  const buttonLoading = useOrganisationStore((state) => state.buttonLoading);

  // ─── Delete Handler ────────────────────────────────────────
  const handleDelete = useCallback(async () => {
    // add delete API
    if (!selectedOrganisation?.code) return;

    deleteOrganisation(selectedOrganisation.code);
  }, [selectedOrganisation, deleteOrganisation]);

  // ─── Render ────────────────────────────────────────────────
  return (
    <Modal size="small" visible={showModal} onCancel={closeModal}>
      <div className={styles.container}>
        <h2>{t("delete_organisation_title")}</h2>

        <p className={classNames(styles.p2, styles.g400)}>
          {t("delete_organisation_substitle")}
        </p>

        <div className={styles.button_container}>
          <PrimaryButton
            onClick={handleDelete}
            disabled={buttonLoading}
            buttonType={PrimaryButtonType.DANGER}
            id="create_contract_success_copy_link">
            {buttonLoading ? <Spinner /> : t("remove")}
          </PrimaryButton>

          <OutlinedButton onClick={closeModal} disabled={buttonLoading}>
            {t("no")}
          </OutlinedButton>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteModal;
